import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material';
import { api } from '@/store/api';
import { useAppSelector } from '@/store/hooks';
import type { DisplayCall } from './CallHistoryCardTypes';
import { CallHistoryCallsSection } from './CallHistoryCallsSection';
import { CallHistoryCallDetailsDialog } from './CallHistoryCallDetailsDialog';
import { CallHistoryCaseDetailsDialog } from './CallHistoryCaseDetailsDialog';
import { CallHistoryCreateCaseDialog } from './CallHistoryCreateCaseDialog';
import { Paper, Typography, Box, Button, Snackbar } from '@gapartners/ui';
import { SelectField } from '@gapartners/ui/select';
import {
  callHistoryItemToDisplayCall,
  getCaseStatusColor,
  getOutcomeColor,
} from './callHistoryUtils';

const CALLS_PER_PAGE = 20;

type CallHistoryFilterMode = 'all' | 'workspace';

export function CallHistoryCard() {
  const contactId = useAppSelector(state => state.call.contactId);
  const directConnectLinkedWorkspaceId = useAppSelector(
    state => state.call.directConnectLinkedWorkspaceId
  );
  const customerWorkSpaceID = useAppSelector(
    state => state.customer.workSpaceID
  );
  const activeWorkspaceId =
    directConnectLinkedWorkspaceId ?? customerWorkSpaceID;

  const [selectedCaseStatuses, setSelectedCaseStatuses] = useState<string[]>(
    []
  );
  const [selectedCall, setSelectedCall] = useState<DisplayCall | null>(null);
  const [filterMode, setFilterMode] = useState<CallHistoryFilterMode>('all');
  const [agentCalls, setAgentCalls] = useState<DisplayCall[]>([]);
  const [workspaceCalls, setWorkspaceCalls] = useState<DisplayCall[]>([]);
  const [agentCursor, setAgentCursor] = useState<string | undefined>(undefined);
  const [workspaceCursor, setWorkspaceCursor] = useState<string | undefined>(
    undefined
  );
  const [agentHasLoadedInitial, setAgentHasLoadedInitial] = useState(false);
  const [workspaceHasLoadedInitial, setWorkspaceHasLoadedInitial] =
    useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const [selectedCaseContactId, setSelectedCaseContactId] = useState<
    string | null
  >(null);
  const [selectedCaseWorkspaceId, setSelectedCaseWorkspaceId] = useState<
    number | null
  >(null);
  const [isCreateCaseOpen, setIsCreateCaseOpen] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const { t } = useTranslation();

  const {
    useGetCaseByIdQuery,
    useGetCallsByAgentQuery,
    useGetCallsByWorkspaceQuery,
    useLazyGetCallByContactIdQuery,
  } = api;

  const [getCallByContactId] = useLazyGetCallByContactIdQuery();

  const showWorkspaceFilter = Boolean(contactId && activeWorkspaceId);
  const isWorkspaceMode = filterMode === 'workspace' && showWorkspaceFilter;

  const {
    data: agentCallsResponse,
    isLoading: isAgentLoading,
    isFetching: isAgentFetching,
    error: agentError,
    refetch: refetchAgentCalls,
  } = useGetCallsByAgentQuery(
    {
      limit: CALLS_PER_PAGE,
      cursor: agentCursor,
    },
    { skip: isWorkspaceMode }
  );

  const {
    data: workspaceCallsResponse,
    isLoading: isWorkspaceLoading,
    isFetching: isWorkspaceFetching,
    error: workspaceError,
    refetch: refetchWorkspaceCalls,
  } = useGetCallsByWorkspaceQuery(
    {
      workspaceId: activeWorkspaceId!,
      limit: CALLS_PER_PAGE,
      cursor: workspaceCursor,
    },
    { skip: !isWorkspaceMode || !activeWorkspaceId }
  );

  const callsResponse = isWorkspaceMode
    ? workspaceCallsResponse
    : agentCallsResponse;
  const isLoading = isWorkspaceMode ? isWorkspaceLoading : isAgentLoading;
  const isFetching = isWorkspaceMode ? isWorkspaceFetching : isAgentFetching;
  const error = isWorkspaceMode ? workspaceError : agentError;
  const refetch = isWorkspaceMode ? refetchWorkspaceCalls : refetchAgentCalls;
  const allCalls = isWorkspaceMode ? workspaceCalls : agentCalls;
  const cursor = isWorkspaceMode ? workspaceCursor : agentCursor;
  const hasLoadedInitial = isWorkspaceMode
    ? workspaceHasLoadedInitial
    : agentHasLoadedInitial;

  const {
    data: caseData,
    isLoading: isCaseLoading,
    error: caseError,
  } = useGetCaseByIdQuery(
    { caseId: selectedCaseId! },
    { skip: selectedCaseId === null }
  );

  useEffect(() => {
    if (!showWorkspaceFilter && filterMode === 'workspace') {
      setFilterMode('all');
    }
  }, [showWorkspaceFilter, filterMode]);

  useEffect(() => {
    if (!callsResponse?.calls) return;

    const setCalls = isWorkspaceMode ? setWorkspaceCalls : setAgentCalls;
    const setHasLoaded = isWorkspaceMode
      ? setWorkspaceHasLoadedInitial
      : setAgentHasLoadedInitial;

    if (!hasLoadedInitial) {
      setCalls(callsResponse.calls.map(callHistoryItemToDisplayCall));
      setHasLoaded(true);
    } else if (cursor) {
      setCalls(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        const newCalls = callsResponse.calls
          .map(callHistoryItemToDisplayCall)
          .filter(c => !existingIds.has(c.id));
        return [...prev, ...newCalls];
      });
    }
  }, [callsResponse, cursor, hasLoadedInitial, isWorkspaceMode]);

  const hasMore = callsResponse?.hasMore ?? false;
  const nextCursor = callsResponse?.nextCursor ?? null;

  const availableCaseStatuses = useMemo(() => {
    const statuses = new Set<string>();
    allCalls.forEach(call => {
      if (call.caseStatus) {
        statuses.add(call.caseStatus);
      }
    });
    return Array.from(statuses)
      .sort()
      .map(status => ({ value: status, label: status }));
  }, [allCalls]);

  const displayCalls = useMemo(() => {
    return allCalls
      .filter(call => {
        if (selectedCaseStatuses.length === 0) return true;
        if (!call.caseStatus) return false;
        return selectedCaseStatuses.includes(call.caseStatus);
      })
      .sort(
        (a, b) =>
          new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
      );
  }, [allCalls, selectedCaseStatuses]);

  const refreshSelectedCall = useCallback(
    async (contactIdToRefresh: string) => {
      const callDetails = await getCallByContactId({
        contactId: contactIdToRefresh,
      }).unwrap();
      setSelectedCall(callHistoryItemToDisplayCall(callDetails));
    },
    [getCallByContactId]
  );

  const handleLoadMore = () => {
    if (!nextCursor) return;
    if (isWorkspaceMode) {
      setWorkspaceCursor(nextCursor);
    } else {
      setAgentCursor(nextCursor);
    }
    setSelectedCaseStatuses([]);
  };

  const handleCaseStatusChange = (e: SelectChangeEvent<unknown>) => {
    setSelectedCaseStatuses(e.target.value as string[]);
  };

  const handleReload = () => {
    setSelectedCall(null);
    if (isWorkspaceMode) {
      setWorkspaceCursor(undefined);
      setWorkspaceCalls([]);
      setWorkspaceHasLoadedInitial(false);
    } else {
      setAgentCursor(undefined);
      setAgentCalls([]);
      setAgentHasLoadedInitial(false);
    }
    setSelectedCaseStatuses([]);
    void refetch();
  };

  const handleToggleWorkspaceFilter = () => {
    setSelectedCaseStatuses([]);
    setFilterMode(prev => (prev === 'all' ? 'workspace' : 'all'));
  };

  const handleCallClick = (call: DisplayCall) => {
    setSelectedCall(call);
  };

  const handleCloseCallDialog = () => {
    setSelectedCall(null);
    setShowTranscript(false);
    setIsCreateCaseOpen(false);
  };

  const handleCloseCaseDialog = () => {
    setSelectedCaseId(null);
    setSelectedCaseContactId(null);
    setSelectedCaseWorkspaceId(null);
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} color="text.primary">
            {t('components.contactCenter.callHistory')} ({displayCalls.length})
          </Typography>

          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={handleReload}
            loading={isFetching}
            disabled={isFetching}
            sx={{ minWidth: 'auto', px: 1.25, py: 0.5, fontSize: '0.75rem' }}
          >
            {isFetching ? t('common.loading') : t('common.refresh')}
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 'fit-content' }}
          >
            {t('common.filter')}:
          </Typography>
          <SelectField
            multiple
            fullWidth={true}
            variant="outlined"
            value={selectedCaseStatuses}
            options={availableCaseStatuses}
            onChange={handleCaseStatusChange}
            label={
              selectedCaseStatuses.length === 0 ? t('common.all') : undefined
            }
            sx={{
              minWidth: 180,
              maxWidth: 240,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                minHeight: '36px',
                '& .MuiOutlinedInput-input': {
                  padding: '6px 12px',
                },
              },
            }}
          />
          {showWorkspaceFilter && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={handleToggleWorkspaceFilter}
              disabled={isFetching}
              sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
            >
              {isWorkspaceMode
                ? t('components.contactCenter.showAllCalls')
                : t('components.contactCenter.showCustomerCalls')}
            </Button>
          )}
        </Box>

        <CallHistoryCallsSection
          displayCalls={displayCalls}
          isLoading={isLoading}
          hasLoadedInitial={hasLoadedInitial}
          error={error}
          isFetching={isFetching}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          onCallClick={handleCallClick}
          onOpenCaseId={(caseId, contactId, workspaceId) => {
            setSelectedCaseId(caseId);
            setSelectedCaseContactId(contactId);
            setSelectedCaseWorkspaceId(workspaceId ?? null);
          }}
          getOutcomeColor={getOutcomeColor}
          getCaseStatusColor={getCaseStatusColor}
        />
      </Paper>

      <CallHistoryCallDetailsDialog
        selectedCall={selectedCall}
        showTranscript={showTranscript}
        setShowTranscript={value => setShowTranscript(value)}
        onClose={handleCloseCallDialog}
        getOutcomeColor={getOutcomeColor}
        getCaseStatusColor={getCaseStatusColor}
        onOpenCaseId={(caseId, contactId) => {
          setSelectedCaseId(caseId);
          setSelectedCaseContactId(contactId);
          setSelectedCaseWorkspaceId(
            selectedCall?.workspaceId ? Number(selectedCall.workspaceId) : null
          );
        }}
        onOpenCreateCase={() => setIsCreateCaseOpen(true)}
        onCustomerLinked={() => {
          if (!selectedCall) return;
          void refreshSelectedCall(selectedCall.contactId);
        }}
        enableCustomerLink
      />

      <CallHistoryCaseDetailsDialog
        selectedCaseId={selectedCaseId}
        contactId={selectedCaseContactId}
        workspaceId={selectedCaseWorkspaceId}
        onClose={handleCloseCaseDialog}
        isCaseLoading={isCaseLoading}
        caseError={caseError}
        caseData={caseData}
      />

      <CallHistoryCreateCaseDialog
        selectedCall={selectedCall}
        open={isCreateCaseOpen}
        onClose={() => setIsCreateCaseOpen(false)}
        onCreated={() => {
          if (!selectedCall) return;
          void refreshSelectedCall(selectedCall.contactId);
        }}
      />

      <Snackbar
        open={!!toast}
        onClose={() => setToast(null)}
        message={toast?.message}
        variant="toast"
        severity={toast?.type ?? 'info'}
        autoHideDuration={3000}
      />
    </>
  );
}
